import { EntityBase } from "./EntityBase";

/**
 * HTTP Method enum for API endpoints
 */
export enum HttpMethod {
    GET = "GET",
    POST = "POST",
    PUT = "PUT",
    PATCH = "PATCH",
    DELETE = "DELETE",
    OPTIONS = "OPTIONS",
    HEAD = "HEAD",
}

/**
 * Endpoint Access Level
 */
export enum EndpointAccessLevel {
    PUBLIC = "public",
    PRIVATE = "private",
    PROTECTED = "protected",
}


/**
 * Endpoint Request Input Structure (Consolidated)
 */
export interface EndpointInput {
    /** Path parameters (e.g., :id in /api/users/:id) */
    path?: Array<{
        name: string;
        type: string;
        required: boolean;
        description?: string;
    }>;

    /** Query parameters this endpoint accepts */
    query?: Array<{
        name: string;
        type: string;
        required: boolean;
        description?: string;
        defaultValue?: unknown;
    }>;

    /** Request headers */
    headers?: Array<{
        name: string;
        type: string;
        required: boolean;
        description?: string;
        defaultValue?: unknown;
    }>;

    /** Request body schema (for POST, PUT, PATCH) */
    body?: {
        contentType: string; // "application/json", "multipart/form-data", etc.
        schema: Record<string, unknown> | string; // JSON schema or reference
        required: boolean;
        description?: string;
    };
}

/**
 * Endpoint Response Output Structure (Consolidated)
 */
export interface EndpointOutput {
    /** Response schemas for different status codes */
    responses: Array<{
        statusCode: number;
        description?: string;
        schema?: Record<string, unknown> | string;
        contentType?: string;
        /** Source of this response - 'endpoint' for direct endpoint responses, 'middleware' for middleware-injected responses */
        source?: 'endpoint' | 'middleware';
        /** Name of the middleware that produces this response (only when source is 'middleware') */
        sourceName?: string;
    }>;
}

/**
 * Endpoint Interface
 * Represents an API endpoint - the final layer that aggregates all entities
 * Endpoints are the only entities that can take all types as dependencies
 */
export interface EndPointInterface extends EntityBase {
    /** HTTP method (GET, POST, PUT, PATCH, DELETE, etc.) */
    method: HttpMethod;

    /** Route path (e.g., "/api/users/:id", "/api/orders") */
    path: string;

    /** Full route path with base URL (e.g., "/api/users/:id") */
    fullPath?: string;

    /** Endpoint name/identifier */
    name: string;

    /** Access level (public, private, protected) */
    accessLevel: EndpointAccessLevel;

    /** Whether authentication is required */
    requiresAuth: boolean;

    /** Whether this endpoint is enabled */
    enabled: boolean;

    // ==================== DEPENDENCIES ====================

    /** Function ID - PRIMARY: Reference to function node that handles this endpoint's logic */
    functionId?: string;

    /** Middleware IDs - PRIMARY: Ordered array of middleware IDs (execution order matters) */
    middlewareIds?: string[];

    /** Model IDs required by this endpoint (for edge creation and schema references) */
    modelsRequired?: string[];

    /** Associated database model name - DEPRECATED: Use modelsRequired instead, but kept for mapping logic */
    /** @deprecated Use modelsRequired array instead */
    modelName?: string;

    /** Service IDs required by this endpoint (for edge creation) */
    servicesRequired?: string[];

    // ==================== REQUEST STRUCTURE ====================

    /** Consolidated request input parameters */
    inputs?: EndpointInput;

    // ==================== RESPONSE STRUCTURE ====================

    /** Consolidated response output structure */
    outputs?: EndpointOutput;

    /** Tags for API documentation (Swagger/OpenAPI) */
    tags?: string[];

    /** Rate limiting configuration for this endpoint */
    rateLimit?: {
        windowMs: number;
        maxRequests: number;
    };

    /** CORS configuration for this endpoint */
    cors?: {
        allowedOrigins: string[];
        allowedMethods: string[];
        allowedHeaders: string[];
        allowCredentials: boolean;
    };

    /** Implementation steps/instructions for this endpoint */
    implementationSteps?: string[];

    /** File path where this endpoint's code is generated */
    filePath?: string;

    /** File name for this endpoint's generated code */
    fileName?: string;

    /**
     * RBAC roles allowed to access this endpoint. 
     * If empty or undefined, access is determined by accessLevel (public/private).
     * If roles are specified, access is restricted to users with those roles.
     */
    access?: string[];

}

